/* Gift finder (sections/kc-gift-finder.liquid). Three rows of chips pick
   who / vibe / budget. Each vibe is a collection: kawaii-catalog.js loads
   all of its in-stock products (the vibe on show as the section comes
   near, the others when a chip is pointed at or pressed), and the picks
   are drawn at random from those under the budget (or from the cheapest,
   with a note, when none are), never one this shopper was shown lately,
   even on an earlier visit. They show as many at a time as the grid has
   columns (--kc-gift-cols, set by CSS); Shuffle draws the next ones.

   Liquid rendered the starting picks with kc-product-card from the first
   products it can see; they give way to a random draw as soon as the
   collection has loaded (quietly, unless the section is already on
   screen). If it can't be loaded, they stay; a vibe or budget picked
   meanwhile shows no cards, just its "All {vibe} gifts" link. New cards
   are cloned from the section's <template>, a copy of the card snippet's
   markup. */
if (!customElements.get('kc-gift-finder')) {
  const WIDTHS = [180, 240, 300, 360, 450, 540, 720];
  const BLIND = /blind[\s-]?box/i;
  // When nothing fits the budget, the picks come from this many cheapest.
  const CLOSEST = 12;

  class KcGiftFinder extends HTMLElement {
    connectedCallback() {
      if (this.grid) return;

      this.grid = this.querySelector('.kc-gift__grid');
      this.template = this.querySelector('template[data-kc-gift-card]');
      this.summary = this.querySelector('.kc-gift__sum');
      this.note = this.querySelector('.kc-gift__note');
      this.allLink = this.querySelector('.kc-gift__all');
      this.shuffleButton = this.querySelector('.kc-gift__shuf');
      if (!this.grid || !this.template) return;

      // Per vibe: its whole collection once loaded, and the request.
      this.full = [];
      this.loads = [];
      this.shown = [];
      this.ticket = 0;
      const days = Number(this.dataset.newDays) || 0;
      this.newSince = days > 0 ? Date.now() / 1000 - days * 86400 : Infinity;

      this.chips = {};
      this.state = {};
      this.querySelectorAll('[data-kc-group]').forEach((group) => {
        const name = group.dataset.kcGroup;
        const chips = Array.from(group.querySelectorAll('button'));
        this.chips[name] = chips;
        this.state[name] = Math.max(0, chips.findIndex((chip) => chip.getAttribute('aria-pressed') === 'true'));
        chips.forEach((chip, index) => chip.addEventListener('click', () => this.pick(name, index)));
      });
      // Pointing at a vibe fetches its collection before the tap.
      (this.chips.vibe || []).forEach((chip, index) => {
        chip.addEventListener('pointerenter', () => this.load(index));
        chip.addEventListener('focus', () => this.load(index));
      });

      if (this.shuffleButton) {
        this.shuffleButton.addEventListener('click', () => {
          this.touched = true;
          this.render('more', true);
        });
      }

      /* Remember what went into the cart, so rebuilt cards still say so. */
      this.added = new Set();
      this.onAdded = (event) => {
        const item = event.detail && event.detail.product;
        if (item) this.added.add(String(item.variant_id || item.id));
      };
      document.addEventListener('ajaxProduct:added', this.onAdded);

      // Shuffle starts as Liquid left it, until the collection is in.
      this.columns = this.readColumns();

      /* The column count follows the grid's width (a container query). */
      this.resizeObserver = new ResizeObserver(() => {
        const columns = this.readColumns();
        if (columns === this.columns) return;
        this.columns = columns;
        this.render('fit', false);
      });
      this.resizeObserver.observe(this.grid);

      this.preloadWhenNear();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.observer) this.observer.disconnect();
      if (this.onAdded) document.removeEventListener('ajaxProduct:added', this.onAdded);
    }

    /* Load the vibe on show once the section is about to be seen (after the
       page has loaded), then swap Liquid's starting picks for a draw. */
    preloadWhenNear() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.observer.disconnect();
          const start = () => {
            this.load(this.state.vibe).then((list) => {
              if (list && !this.touched) this.render('draw', this.onScreen());
            });
          };
          if (document.readyState === 'complete') (window.requestIdleCallback || setTimeout)(start);
          else window.addEventListener('load', start, { once: true });
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this);
    }

    onScreen() {
      const box = this.grid.getBoundingClientRect();
      return box.bottom > 0 && box.top < window.innerHeight;
    }

    /* A vibe's whole collection: every in-stock product with a photo, in
       the pools' short keys. Null when it can't be had (tried again next
       time). */
    load(index) {
      if (this.full[index]) return Promise.resolve(this.full[index]);
      const chip = (this.chips.vibe || [])[index];
      const catalog = window.kcCatalog;
      if (!chip || !chip.dataset.url || !catalog) return Promise.resolve(null);
      if (!this.loads[index]) {
        this.loads[index] = catalog
          .load(catalog.feed(chip.dataset.url))
          .then((products) => {
            const list = products.filter((p) => p.image).map((p) => this.fromFeed(p));
            if (list.length) this.full[index] = list;
            return this.full[index] || null;
          })
          .catch(() => {
            this.loads[index] = null;
            return null;
          });
      }
      return this.loads[index];
    }

    fromFeed(p) {
      return {
        url: p.url,
        t: p.title,
        u: p.url,
        p: p.price,
        c: p.compare || 0,
        f: Boolean(p.varies),
        i: window.kcCatalog.sized(p.image, 720),
        w: p.w || 0,
        h: p.h || 0,
        v: p.variant,
        s: Boolean(p.single),
        n: (p.published || 0) > this.newSince,
        b: BLIND.test(p.title)
      };
    }

    readColumns() {
      const value = parseInt(getComputedStyle(this.grid).getPropertyValue('--kc-gift-cols'), 10);
      return value > 0 ? value : 3;
    }

    selected(group) {
      const chips = this.chips[group] || [];
      return chips[this.state[group]] || chips[0] || null;
    }

    pick(group, index) {
      if (this.state[group] === index) return;
      this.touched = true;
      this.state[group] = index;
      this.chips[group].forEach((chip, i) => chip.setAttribute('aria-pressed', String(i === index)));
      this.render('draw', true);
    }

    /* The chosen vibe's products under the chosen budget; or, when none
       fit, the cheapest few. */
    currentPicks() {
      const pool = this.full[this.state.vibe] || [];
      const budget = this.selected('budget');
      const max = budget ? Number(budget.dataset.max) || 0 : 0;
      const fits = max > 0 ? pool.filter((p) => p.p < max) : pool.slice();
      if (fits.length || !pool.length) return { picks: fits, closest: false };
      return { picks: pool.slice().sort((a, b) => a.p - b.p).slice(0, CLOSEST), closest: true };
    }

    updateShuffle(count) {
      if (this.shuffleButton) this.shuffleButton.hidden = count <= this.columns;
    }

    /* `mode`: 'draw' new picks (a chip), 'more' (Shuffle) or 'fit' the same
       picks to a new column count. Waits for the vibe's collection when
       it's on its way. */
    render(mode, animate) {
      const ticket = ++this.ticket;
      const vibe = this.state.vibe;
      if (this.full[vibe]) {
        this.paint(mode, animate);
        return;
      }
      this.grid.setAttribute('aria-busy', 'true');
      this.load(vibe).then(() => {
        if (ticket !== this.ticket) return;
        this.grid.removeAttribute('aria-busy');
        this.paint(mode, animate);
      });
    }

    draw(list, count, avoid) {
      const vibe = this.selected('vibe');
      const urls = avoid.map((p) => p.url);
      const catalog = window.kcCatalog;
      if (!catalog) return list.filter((p) => !urls.includes(p.url)).slice(0, count);
      return catalog.draw(`gift:${vibe ? vibe.dataset.url : ''}`, list, count, urls);
    }

    paint(mode, animate) {
      if (!this.full[this.state.vibe]) {
        // The collection couldn't be loaded. Shuffle and a new column count
        // keep the cards on show (CSS hides a third in two columns); a new
        // choice can't be filled, so it shows none, just its link.
        if (mode === 'draw') {
          this.shown = [];
          this.grid.replaceChildren();
          this.label(false);
        }
        this.updateShuffle(0);
        return;
      }

      const { picks, closest } = this.currentPicks();
      const count = this.columns;
      let shown;
      if (mode === 'fit') {
        const still = this.shown.filter((p) => picks.includes(p)).slice(0, count);
        shown = still.length < count ? still.concat(this.draw(picks, count - still.length, still)) : still;
      } else {
        shown = this.draw(picks, count, this.shown);
      }
      this.shown = shown;

      this.grid.classList.toggle('is-swapping', animate);
      this.grid.replaceChildren(...shown.map((p) => this.card(p)));
      this.label(closest);
      this.updateShuffle(picks.length);
    }

    /* The summary, the budget note and the "All {vibe} gifts" link. */
    label(closest) {
      const who = this.selected('who');
      const vibe = this.selected('vibe');
      const budget = this.selected('budget');
      const budgetLabel = budget ? budget.textContent.trim().toLowerCase() : '';

      if (this.summary) {
        this.summary.textContent = [
          who ? who.dataset.summary : '',
          vibe ? vibe.textContent.trim().toLowerCase() : '',
          budgetLabel
        ]
          .filter(Boolean)
          .join(' · ');
      }

      if (this.note) {
        this.note.hidden = !closest;
        this.note.textContent = closest ? (this.note.dataset.text || '').replace('[budget]', budgetLabel) : '';
      }

      if (this.allLink && vibe) {
        this.allLink.href = vibe.dataset.url;
        const label = this.allLink.querySelector('.kc-gift__all-t');
        if (label) label.textContent = (this.allLink.dataset.label || '').replace('[vibe]', vibe.dataset.name || '');
      }
    }

    money(cents) {
      const theme = window.theme;
      if (!theme || !theme.Currency || !theme.shopSettings) return `$${(cents / 100).toFixed(2)}`;
      const formatted = theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat);
      if (!/[<&]/.test(formatted)) return formatted;
      /* The shop's money format may carry markup or entities; keep the text. */
      return new DOMParser().parseFromString(formatted, 'text/html').body.textContent;
    }

    /* Fill a copy of the card template (kc-product-card's markup). */
    card(p) {
      const item = document.importNode(this.template.content, true).firstElementChild;
      const price = (p.f ? 'from ' : '') + this.money(p.p);

      const media = item.querySelector('.kc-card__media');
      media.href = p.u;

      const tag = media.querySelector('.kc-ptag__in');
      if (p.c > p.p) {
        const was = document.createElement('s');
        was.textContent = this.money(p.c);
        tag.append(was);
      }
      tag.append(price);

      const img = media.querySelector('img');
      const placeholder = media.querySelector('svg');
      if (p.i) {
        const width = p.w || 720;
        const height = p.h || width;
        const shown = Math.min(720, width);
        const sizes = WIDTHS.filter((w) => w <= width);
        if (width < 720 && !sizes.includes(width)) sizes.push(width);
        img.width = shown;
        img.height = Math.round((shown * height) / width);
        img.srcset = sizes.map((w) => `${p.i.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`).join(', ');
        img.src = p.i;
        if (placeholder) placeholder.remove();
      } else {
        img.remove();
      }

      if (!p.n) media.querySelector('.kc-sticker:not(.kc-sticker--blind)').remove();
      if (!p.b) media.querySelector('.kc-sticker--blind').remove();

      /* Single-variant products add straight to the cart; others link to
         their page to choose options. */
      item.querySelectorAll('add-to-cart').forEach((add) => {
        if (!p.s) {
          add.remove();
          return;
        }
        add.dataset.variantId = p.v;
        if (this.added.has(String(p.v))) add.classList.add('is-added');
        const button = add.querySelector('button');
        button.setAttribute('aria-label', button.getAttribute('aria-label') + p.t);
      });
      // (the quick-view magnifier is an a.kc-rnd too: it stays, filled below)
      item.querySelectorAll('a.kc-rnd:not([data-kc-qv]), a.kc-card__pill:not([data-kc-qv])').forEach((link) => {
        if (p.s) {
          link.remove();
          return;
        }
        link.href = p.u;
        if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', link.getAttribute('aria-label') + p.t);
      });

      /* Quick view (when on: the magnifier on the photo) opens every product,
         whatever its options. */
      item.querySelectorAll('a[data-kc-qv]').forEach((link) => {
        link.href = p.u;
        link.setAttribute('aria-label', link.getAttribute('aria-label') + p.t);
      });

      const title = item.querySelector('.kc-card__title');
      title.href = p.u;
      title.textContent = p.t;
      item.querySelector('.kc-vh').textContent = price;

      return item;
    }
  }

  customElements.define('kc-gift-finder', KcGiftFinder);
}
